/**
 * The duel (ui/duelHud.ts, docs/research/C0_PVP.md): the sounds the bout adds to the combat family. The wind-up
 * warnings (a break, a power move, a charge) are `combat.breakWindup` and friends (sfx/events.ts castStart); these are
 * the rest of what a fighter reads by ear: the countdown, a parry (brighter than a block), an opening (the other is
 * staggered), the other raising a guard, and the end of a round and of the bout.
 */
import { registerSfx } from '../registry';
import { bowl, chime, gong, note, ROOT_D } from '../voices';

registerSfx('duel.tick', { label: 'นับถอยหลังประลอง', cat: 'combat', vary: 0, gap: 0.2 }, (k) => {
  k.tone({ f: note(ROOT_D, 0), dur: 0.12, vol: 0.12, attack: 0.002 });
  k.thump({ f0: 160, f1: 90, dur: 0.1, vol: 0.18 });
});

registerSfx('duel.go', { label: 'เริ่มประลอง', cat: 'combat', vary: 0, gap: 0.3, preview: 1 }, (k) => {
  gong(k, { f: 130, dur: 0.9, vol: 0.3 });
  chime(k, { f: note(ROOT_D, 2) * 2, at: 0.02, vol: 0.12 });
  k.noise({ dur: 0.18, vol: 0.2, attack: 0.002, type: 'highpass', f: 2500 });
});

registerSfx('duel.parry', { label: 'ปัดป้อง (จังหวะพอดี)', cat: 'combat', vary: 0.02, gap: 0.1, preview: 1 }, (k) => {
  // A block that lands on the beat: the clash, then a bright bell over it, so it is never taken for a plain block.
  k.noise({ dur: 0.02, vol: 0.38, attack: 0.001, type: 'highpass', f: 4200 });
  k.metal({ f: 1750, dur: 0.6, vol: 0.14 });
  k.thump({ f0: 260, f1: 120, dur: 0.1, vol: 0.22 });
  chime(k, { f: note(ROOT_D, 4) * 4, at: 0.03, vol: 0.14 });
  chime(k, { f: note(ROOT_D, 7) * 4, at: 0.09, vol: 0.1 });
});

registerSfx('duel.opening', { label: 'ฝ่ายตรงข้ามเสียหลัก (โอกาส)', cat: 'combat', vary: 0.02, gap: 0.5 }, (k) => {
  // A short rising two-note blip: "now".
  k.tone({ type: 'triangle', f: 700, dur: 0.07, vol: 0.1, attack: 0.002 });
  k.tone({ type: 'triangle', f: 1050, at: 0.07, dur: 0.1, vol: 0.1, attack: 0.002 });
});

registerSfx('duel.guardUp', { label: 'ฝ่ายตรงข้ามตั้งรับ', cat: 'combat', vary: 0.03, gap: 0.5 }, (k) => {
  k.tone({ type: 'sine', f: 330, f1: 280, dur: 0.12, vol: 0.08, attack: 0.004 });
  k.noise({ dur: 0.06, vol: 0.06, attack: 0.005, f: 900, q: 1 });
});

registerSfx('duel.roundWin', { label: 'ชนะรอบ', cat: 'combat', vary: 0, gap: 0.5, preview: 1.4 }, (k) => {
  chime(k, { f: note(ROOT_D, 0) * 2, vol: 0.14 });
  chime(k, { f: note(ROOT_D, 2) * 2, at: 0.12, vol: 0.14 });
  chime(k, { f: note(ROOT_D, 4) * 2, at: 0.24, vol: 0.16 });
});

registerSfx('duel.roundLose', { label: 'แพ้รอบ', cat: 'combat', vary: 0, gap: 0.5, preview: 1.4 }, (k) => {
  bowl(k, { f: note(ROOT_D, 4), dur: 0.9, vol: 0.12 });
  k.thump({ f0: 120, f1: 50, at: 0.05, dur: 0.4, vol: 0.25 });
});

registerSfx('duel.win', { label: 'ชนะประลอง', cat: 'combat', vary: 0, gap: 1, preview: 2.4 }, (k) => {
  gong(k, { f: 150, dur: 1.4, vol: 0.3 });
  for (let i = 0; i < 5; i++) chime(k, { f: note(ROOT_D, i) * 2, at: 0.1 + i * 0.12, vol: 0.14 });
});

registerSfx('duel.lose', { label: 'แพ้ประลอง', cat: 'combat', vary: 0, gap: 1, preview: 2 }, (k) => {
  gong(k, { f: 90, dur: 1.6, vol: 0.28 });
  bowl(k, { f: note(ROOT_D, 0), at: 0.2, dur: 1.2, vol: 0.1 });
});

registerSfx('duel.stance', { label: 'ตั้งท่าสวนกลับ', cat: 'combat', vary: 0.02, gap: 0.3, preview: 0.8 }, (k) => {
  // A held breath: a low swell and a bright ring that says "do not strike now".
  k.tone({ type: 'sine', f: 220, f1: 330, dur: 0.35, vol: 0.1, attack: 0.05 });
  chime(k, { f: note(ROOT_D, 2) * 4, at: 0.12, vol: 0.09 });
});

registerSfx('duel.guardShatter', { label: 'การ์ดแตก', cat: 'combat', vary: 0.03, gap: 0.5, preview: 0.9 }, (k) => {
  // Glass and a hollow knock: the guard gone.
  k.noise({ color: 'crackle', dur: 0.3, vol: 0.5, attack: 0.002, type: 'highpass', f: 2600, q: 0.6 });
  k.thump({ f0: 140, f1: 50, dur: 0.3, vol: 0.3 });
  for (let n = 0; n < 4; n++) k.tone({ f: k.range(3200, 6000), at: 0.02 + n * 0.04, dur: 0.06, vol: 0.03 });
});
